'use client';

import { formatCents } from '@/lib/format';
import {
  Loader2,
  MapPin,
  CheckCircle2,
  DollarSign,
  AlertTriangle,
  FileText,
} from 'lucide-react';
import { KpiRibbon } from '@/components/KpiRibbon';
import { ActivityFeed } from '@/components/ActivityFeed';
import { ClaudeUsage } from '@/components/ClaudeUsage';
import { InsightsPanel } from '@/components/InsightsPanel';
import { KpiGrid } from '@/components/KpiGrid';
import { NeuralIntake } from '@/components/NeuralIntake';
import { QuickStats } from '@/components/QuickStats';
import { OnboardingChecklist } from '@/components/dashboard/OnboardingChecklist';
import { WeeklyCapacityWidget } from '@/components/dashboard/WeeklyCapacityWidget';
import { RevenueWidget } from '@/components/dashboard/RevenueWidget';
import { CustomerHealthWidget } from '@/components/dashboard/CustomerHealthWidget';
import { CrewSplitAuditWidget } from '@/components/dashboard/CrewSplitAuditWidget';
import { AttentionPanel } from '@/components/AttentionPanel';
import { LeadInbox } from '@/components/dashboard/LeadInbox';
import { NewCustomerQueue } from '@/components/dashboard/NewCustomerQueue';
import { KanbanBoard } from '@/components/KanbanBoard';
import { usePolling } from '@/lib/usePolling';
// ─── Types ─────────────────────────────────────────────

interface RouteEntry {
  id: string;
  name: string | null;
  status: string;
  crew_id: string;
}

interface RouteSummaryData {
  routes: RouteEntry[];
  totalStops: number;
  completedStops: number;
}

interface ArAgingBucket {
  bucket: string;
  count: number;
  total_cents: number;
}

interface InvoiceSummaryData {
  totalCents: number;
  totalCount: number;
}

// ─── Helpers ───────────────────────────────────────────

function todayISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function statusBadge(status: string) {
  switch (status) {
    case 'completed':
      return { bg: 'bg-emerald-500/15', text: 'text-emerald-400', label: 'Completed' };
    case 'in_progress':
      return { bg: 'bg-amber-500/15', text: 'text-amber-400', label: 'In Progress' };
    case 'scheduled':
      return { bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'Scheduled' };
    case 'cancelled':
      return { bg: 'bg-zinc-500/15', text: 'text-zinc-400', label: 'Cancelled' };
    default:
      return { bg: 'bg-zinc-500/15', text: 'text-zinc-400', label: status.replace(/_/g, ' ') };
  }
}

// ─── Today's Routes Summary ────────────────────────────

function TodaysRoutesSummary() {
  const today = todayISO();

  const { data, isLoading, error } = usePolling<RouteSummaryData>({
    url: `/api/field/routes?date=${today}&with_stops=1`,
    intervalMs: 60_000,
    initialValue: { routes: [], totalStops: 0, completedStops: 0 },
    transform: (json) => {
      const routes: RouteEntry[] = (json.routes || []).map((r: Record<string, unknown>) => ({
        id: r.id as string,
        name: r.name as string | null,
        status: r.status as string,
        crew_id: r.crew_id as string,
      }));
      let totalStops = 0;
      let completedStops = 0;
      for (const r of json.routes || []) {
        const stops = (r as Record<string, unknown>).stops as Array<Record<string, unknown>> | undefined;
        if (stops) {
          totalStops += stops.length;
          completedStops += stops.filter((s) => s.status === 'completed').length;
        }
      }
      return { routes, totalStops, completedStops };
    },
  });

  const completionPct = data.totalStops > 0
    ? Math.round((data.completedStops / data.totalStops) * 100)
    : 0;

  return (
    <div className="rounded-xl border border-white/5 bg-zinc-900/60 backdrop-blur-sm p-4 flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-1.5 rounded-md bg-blue-500/10">
          <MapPin className="w-3.5 h-3.5 text-blue-400" />
        </div>
        <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
          Today&apos;s Routes
        </span>
      </div>

      {isLoading && data.routes.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-zinc-600" />
        </div>
      ) : error ? (
        <p className="text-[10px] text-rose-400/70">{error}</p>
      ) : data.routes.length === 0 ? (
        <p className="text-xs text-zinc-500 text-pretty">No routes scheduled today.</p>
      ) : (
        <>
          {/* Headline stats */}
          <div className="flex items-baseline gap-1.5 mb-3">
            <span className="text-xl font-black text-white tabular-nums">
              {data.routes.length}
            </span>
            <span className="text-[10px] text-zinc-400">
              route{data.routes.length !== 1 ? 's' : ''}
            </span>
            <span className="text-zinc-700 mx-1">·</span>
            <span className="text-sm font-bold text-zinc-300 tabular-nums">
              {data.totalStops}
            </span>
            <span className="text-[10px] text-zinc-400">stops</span>
            <span className="text-zinc-700 mx-1">·</span>
            <span className="text-sm font-bold text-emerald-400 tabular-nums">
              {completionPct}%
            </span>
            <CheckCircle2 className="w-3 h-3 text-emerald-500/60" />
          </div>

          {/* Progress bar */}
          <div className="h-1.5 rounded-full bg-zinc-800/80 overflow-hidden mb-3">
            <div
              className="h-full rounded-full bg-emerald-500 transition-colors duration-700 ease-out"
              style={{ width: `${completionPct}%` }}
            />
          </div>

          {/* Route list */}
          <div className="space-y-1.5 flex-1 overflow-y-auto max-h-[120px]">
            {data.routes.map((r) => {
              const badge = statusBadge(r.status);
              return (
                <div key={r.id} className="flex items-center justify-between">
                  <span className="text-xs text-zinc-300 truncate mr-2">
                    {r.name || `Route ${r.id.slice(0, 6)}`}
                  </span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${badge.bg} ${badge.text}`}>
                    {badge.label}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Open Invoice Count ────────────────────────────────

function OpenInvoiceSummary() {
  const { data, isLoading, error } = usePolling<InvoiceSummaryData>({
    url: '/api/field/billing/ar-aging',
    intervalMs: 120_000,
    initialValue: { totalCents: 0, totalCount: 0 },
    transform: (json) => {
      const buckets: ArAgingBucket[] = json.aging || [];
      let totalCents = 0;
      let totalCount = 0;
      for (const b of buckets) {
        totalCents += b.total_cents;
        totalCount += b.count;
      }
      return { totalCents, totalCount };
    },
  });

  const hasOutstanding = data.totalCount > 0;

  return (
    <div className="rounded-xl border border-white/5 bg-zinc-900/60 backdrop-blur-sm p-4 flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-1.5 rounded-md bg-amber-500/10">
          <DollarSign className="w-3.5 h-3.5 text-amber-400" />
        </div>
        <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
          Outstanding AR
        </span>
      </div>

      {isLoading && data.totalCount === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-zinc-600" />
        </div>
      ) : error ? (
        <p className="text-[10px] text-rose-400/70">{error}</p>
      ) : !hasOutstanding ? (
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <span className="text-xs text-emerald-400 font-medium">All clear — $0 outstanding</span>
        </div>
      ) : (
        <>
          {/* Big dollar figure */}
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-2xl font-black text-white tabular-nums">
              {formatCents(data.totalCents, { decimals: 0, locale: true })}
            </span>
          </div>

          {/* Sub-line */}
          <div className="flex items-center gap-1.5">
            <FileText className="w-3 h-3 text-zinc-500" />
            <span className="text-xs text-zinc-400">
              across{' '}
              <span className="text-zinc-200 font-semibold tabular-nums">
                {data.totalCount}
              </span>{' '}
              invoice{data.totalCount !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Warning if large */}
          {data.totalCents > 500_00 && (
            <div className="mt-3 flex items-center gap-1.5 text-amber-400/80">
              <AlertTriangle className="w-3 h-3" />
              <span className="text-[10px] font-medium">Review overdue balances</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main Dashboard Page ───────────────────────────────

export default function DashboardPage() {
  return (
    <div className="flex flex-col h-full bg-bg-base min-h-screen text-zinc-200">
      <div className="flex-1 p-4 md:p-8 max-w-[1700px] mx-auto w-full">
          <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight mb-0.5 text-balance">Home</h1>
          <p className="text-zinc-500 text-[10px] md:text-xs uppercase tracking-[0.18em] font-medium">
            Urban Diamond Lawn + Landscaping
          </p>

        <AttentionPanel />

        {/* KPI Ribbon */}
        <KpiRibbon />

        {/* Onboarding Checklist — auto-hides when fully configured */}
        <OnboardingChecklist />

        {/* Command Center Cards: Capacity + Revenue + Routes + Invoices + Health */}
        <div className="mt-4 grid min-w-0 grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <WeeklyCapacityWidget />
          <RevenueWidget />
          <TodaysRoutesSummary />
          <OpenInvoiceSummary />
          <CustomerHealthWidget />
        </div>

        {/* Crew-split integrity widget — auto-hidden when (a) all-zero
            findings or (b) the user lacks the 'edit:schedule' permission
            (Plan 92-08). Daily 03:35 AM cron from Plan 92-01 emits the
            same findings via email; this is the in-app companion channel. */}
        <CrewSplitAuditWidget />

        {/* New Customer Queue — accepted estimates needing route assignment */}
        <NewCustomerQueue />

        {/* Lead Inbox */}
        <LeadInbox />

        {/* Kanban Board */}
        <div className="mt-8">
          <KanbanBoard />
        </div>

        <section aria-labelledby="operations-insights-heading" className="mt-10 space-y-4">
          <h2 id="operations-insights-heading" className="text-lg font-semibold text-white tracking-tight">
            Operations insights
          </h2>

          <div className="min-w-0">
            <h3 className="mb-3 text-sm font-medium text-zinc-300">Daily KPIs</h3>
            <KpiGrid />
          </div>

          <div className="grid min-w-0 grid-cols-1 items-start gap-4 lg:grid-cols-3">
            <section aria-labelledby="activity-feed-heading" className="min-w-0 overflow-hidden rounded-xl border border-white/5 bg-zinc-900/50">
              <h3 id="activity-feed-heading" className="border-b border-white/5 px-4 py-3 text-sm font-medium text-zinc-300">
                Recent activity
              </h3>
              <div className="max-h-[460px] overflow-y-auto">
                <ActivityFeed />
              </div>
            </section>
            <div className="min-w-0 space-y-4">
              <InsightsPanel />
              <ClaudeUsage />
            </div>
            <div className="min-w-0 space-y-4">
              <QuickStats />
              <NeuralIntake />
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}
